import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Form as FormikForm, Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import {
  AdminUpdateClientInput,
  CapabilityGroup,
  FeaturePlanType,
  useAdminUpdateClientMutation,
} from 'api';
import { Loaders, ValidatedTextFieldNext } from 'components';
import { useCapabilityToggle, useNotify, useProtectedClient } from 'hooks';

import { useAdminUpdateClient } from '../updaters';
import { AccountSettingsSchema } from '../validations';
import {
  ContentDomains,
  Integrations,
  Setting,
  UploadIcon,
  UploadLogo,
} from '../components/AccountSettings';

const useStyles = makeStyles(theme => ({
  '@global body': {
    backgroundColor: theme.palette.common.white,
  },
  dividerPrimary: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.common.black,
    height: 2,
  },
  divider: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
}));

const AccountSettings: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const client = useProtectedClient();
  const notify = useNotify();
  const updateClientMutationOptions = useAdminUpdateClient();
  const isEnterprise = client.feature_plan_type === FeaturePlanType.E;
  const { t } = useTranslation();

  const initialValues: AdminUpdateClientInput = {
    id: client.id,
    image: client.image,
    icon: client.icon,
    title: client.title,
    content_domains: client.content_domains || [],
    allow_user_signup: client.allow_user_signup,
    moderator_can_mark_as_important: client.moderator_can_mark_as_important,
    allow_group_creation: client.allow_group_creation,
    allow_sharing: client.allow_sharing,
  };

  const { mutateAsync: updateClientMutation, isLoading: isUpdateClientLoading } =
    useAdminUpdateClientMutation(updateClientMutationOptions);
  const slackToggle = useCapabilityToggle(
    'UserConnectNetworks.Function.ConnectSlackAccount',
    CapabilityGroup.Slack
  );
  const teamsToggle = useCapabilityToggle(
    'UserConnectNetworks.Function.ConnectMsTeamsAccount',
    CapabilityGroup.Teams
  );

  const isLoading = isUpdateClientLoading || slackToggle.isLoading || teamsToggle.isLoading;

  async function performSave(values: AdminUpdateClientInput) {
    try {
      await slackToggle.save();
      await teamsToggle.save();
      await updateClientMutation({ data: values });

      notify.info({ message: t('components:savedSuccessfully') });
    } catch (error) {
      notify.mutationError();
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={performSave}
      validationSchema={AccountSettingsSchema}
    >
      {({ handleSubmit }) => (
        <FormikForm
          onSubmit={event => {
            event.preventDefault();
            event.stopPropagation();

            handleSubmit();
          }}
        >
          <Grid container alignItems="center">
            <Grid item xs>
              <Typography variant="h4">{t('admin:workspaceSettings')}</Typography>
            </Grid>
            <Grid item>
              <Typography color="textSecondary">
                {t('admin:workspaceAccountId')} {client.id}
              </Typography>
            </Grid>
          </Grid>
          <Divider className={classes.dividerPrimary} />
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle2">{t('common:information')}</Typography>
            </Grid>
            <Grid item xs>
              <ValidatedTextFieldNext
                enableTrim
                fullWidth
                name="title"
                label={t('admin:workspaceName')}
                placeholder={t('admin:workspaceName')}
              />
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle2">{t('common:logo')}</Typography>
            </Grid>
            <Grid item>
              <UploadLogo />
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle2">{t('common:icon')}</Typography>
            </Grid>
            <Grid item>
              <UploadIcon />
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <Grid container>
            <Grid item xs={3}>
              <Grid container alignItems="center">
                <Grid item>
                  <Typography variant="subtitle2">{t('admin:workspaceContentDomains')}</Typography>
                </Grid>
                <Grid item>
                  <Tooltip title={t('admin:workspaceContentDomainTip')}>
                    <IconButton size="small" color="default">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs>
              <ContentDomains />
            </Grid>
          </Grid>
          {isEnterprise && (
            <Fragment>
              <Divider className={classes.divider} />
              <Grid container>
                <Grid item xs={3}>
                  <Typography variant="subtitle2">{t('admin:workspacePrivileges')}</Typography>
                </Grid>
                <Grid item xs>
                  <Grid container direction="column" wrap="nowrap" spacing={2}>
                    <Grid item>
                      <Setting
                        name="allow_user_signup"
                        title={t('admin:workspaceUserSignupTitle')}
                        description={t('admin:workspaceUserSignupDescription')}
                      />
                    </Grid>
                    <Grid item>
                      <Setting
                        name="allow_group_creation"
                        title={t('admin:workspaceGroupCreationTitle')}
                        description={t('admin:workspaceGroupCreationDescription')}
                      />
                    </Grid>
                    <Grid item>
                      <Setting
                        name="allow_sharing"
                        title={t('admin:workspaceAllowSharingTitle')}
                        description={t('admin:workspaceAllowSharingDescription')}
                      />
                    </Grid>
                    <Grid item>
                      <Setting
                        name="moderator_can_mark_as_important"
                        title={t('admin:workspaceUserModeratorImportantTitle')}
                        description={t('admin:workspaceUserModeratorImportantDescription')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          )}
          <Divider className={classes.divider} />
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle2">{t('common:integration_plural')}</Typography>
            </Grid>
            <Grid item xs>
              <Integrations slackToggle={slackToggle} teamsToggle={teamsToggle} />
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <Grid container justify="flex-end">
            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs>
                  <Button onClick={() => history.goBack()} size="large">
                    {t('common:cancel')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    disabled={isLoading}
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                  >
                    {isLoading ? <Loaders.Standard height={26} /> : t('common:save')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FormikForm>
      )}
    </Formik>
  );
};

export default AccountSettings;
